<template>
  <div class="with-side-panel">
    <div class="content-i">
      <div class="content-box">
        <div class="element-wrapper">
          <h6 class="element-header">
            {{ $t('dashboard.dashboard_for') + piniaUserStore().currentUser?.user?.userAccessRightsTranslation }}
          </h6>

          <Menu/>

          <div class="element-box mt-5">
            <div class="row">
              <CoachingButton
                  :enabled="companySettings?.privateCoachingEnabled"
                  route-name="privateCoachingNewAndActiveList"
                  coaching-type="private"
                  label="Einzelcoaching"/>
              <CoachingButton
                  :enabled="companySettings?.caseManagementCoachingEnabled"
                  route-name="caseManagementCoachingNewAndActiveList"
                  coaching-type="caseManagement"
                  label="DRV Fallmanagement"/>
              <CoachingButton
                  :enabled="companySettings?.groupCoachingEnabled"
                  route-name="groupCoachingNewAndActiveList"
                  coaching-type="group"
                  label="Gruppenmaßnahmen"/>
            </div>
          </div>

          <div class="row mt-5">
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CardInfo :label="'Übersicht Standorte'" :value="infoData?.countLocations"/>
            </div>
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CardInfo :label="'Aktive Kunden'" :value="infoData?.coachingTotals?.activeCoaching"/>
            </div>
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CardInfo :label="'New Kunden'" :value="infoData?.coachingTotals?.newCoaching"/>
            </div>
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CardInfo :label="'Abgeschlossen Kunden'" :value="infoData?.coachingTotals?.finishedCoaching"/>
            </div>
          </div>

          <div class="element-box mt-4">
            <div class="row">
              <div class="col-lg-6 col-md-6 col-sm-12">
                <h6 class="mt-3">Standortinformationen</h6>
              </div>
              <div class="col-lg-6 col-md-6 col-sm-12 text-right">
                <ExcelExportButton
                    label="Standortinformationen Excelexport"
                    @click="downloadFile('/dashboard/practice-manager/location-data-excel-export',
                    undefined, '', false)"
                />
              </div>
            </div>

            <div class="row mt-4">
              <div class="col-sm-12">
                <div class="table-responsive">
                  <table class="table table-striped">
                    <thead>
                    <tr>
                      <th>Standort</th>
                      <th> Bundesland</th>
                      <th class="text-right">Aktive Kunden</th>
                      <th class="text-right">Neu Kunden</th>
                      <th class="text-right">Abgeschlossen Kunden</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(locationData) in infoData.dataByLocations" :key="locationData.id">
                      <td>{{ locationData?.locationName }}</td>
                      <td>{{ locationData?.federalState }}</td>
                      <td class="text-right">{{ locationData?.countActiveCoaching }}</td>
                      <td class="text-right">{{ locationData?.countNewCoaching }}</td>
                      <td class="text-right">{{ locationData?.countFinishedCoaching }}</td>
                    </tr>
                    <tr>
                      <td style="font-weight: bolder">gesamt</td>
                      <td style="font-weight: bolder" class="text-right"></td>
                      <td style="font-weight: bolder" class="text-right">{{
                          infoData?.coachingTotals?.activeCoaching
                        }}
                      </td>
                      <td style="font-weight: bolder" class="text-right">{{
                          infoData?.coachingTotals?.newCoaching
                        }}
                      </td>
                      <td style="font-weight: bolder" class="text-right">{{
                          infoData?.coachingTotals?.finishedCoaching
                        }}
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
                <Pagination
                    v-if="infoData?.pagination?.total_pages ?? 0 > 1"
                    :totalPages="infoData?.pagination?.total_pages"
                    :currentPage="infoData?.pagination?.current_page"
                    @pagechanged="getInfoData"/>
              </div>
            </div>
          </div>

        </div>
      </div>
      <FormSidebar>
        <template #top>
          <h6 class="element-header">
            Sidebar
          </h6>
        </template>

        <div class="element-box mt-5">
          <h6>Kunden info</h6>
          <VueApexCharts width="100%" type="donut" :options="chartOptions" :series="seriesCoachingData"/>
        </div>
        <div class="element-box">
          <h6>Standorte nach Bundesländern</h6>
          <div v-for="user of infoData?.countOfficeByFederalState" v-bind:key="user?.name"
               class="d-flex justify-content-between">
            <span>{{ user?.federalState }}</span>
            <span>{{ user?.count }}</span>
          </div>
        </div>
      </FormSidebar>
    </div>
  </div>
</template>

<script setup>
import dashboardPracticeManagerService from '@/api/Dashboard/PracticeManager';
import CardInfo from '@/components/card/CardInfo.vue';
import CoachingButton from '@/components/button/CoachingButton.vue';
import FormSidebar from '@/components/FormSidebar.vue';
import {CloseToast, ErrorToast} from '@/components/VueToast';
import {downloadFile} from '@/shared/functions';
import {optionsChartDonut} from '@/shared/utils/apex';
import {piniaCompanySettingsStore} from '@/store/piniaCompanySettingsStore';
import {piniaUserStore} from '@/store/piniaUserStore';
import Menu from '@/views/App/Dashboard/PracticeManager/Menu.vue';
import {computed, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import VueApexCharts from 'vue3-apexcharts';
import Pagination from "@/components/Pagination.vue";
import ExcelExportButton from "@/components/ExcelExportButton.vue";

const {t} = useI18n();

const piniaCompanySettingsState = piniaCompanySettingsStore();
const companySettings = computed(() => piniaCompanySettingsState.companySettings);

const chartOptions = ref(optionsChartDonut);
const seriesCoachingData = ref([]);

const infoData = ref({});

async function getInfoData(page = 1, perPage = 15) {
  try {

    const perPageRes = infoData.value?.pagination?.items_per_page;

    const {data} = await dashboardPracticeManagerService.getInfoData({
      page: page,
      limit: perPage ?? perPageRes,
    });

    infoData.value = data.data;

    seriesCoachingData.value = Object.values(data.data.coachingTotals);

    const totals =
        infoData?.value?.coachingTotals?.activeCoaching +
        infoData?.value?.coachingTotals?.newCoaching +
        infoData?.value?.coachingTotals?.finishedCoaching;

    chartOptions.value = {
      ...chartOptions.value,
      responsive: [{
        breakpoint: 2480,
        options: {
          chart: {
            width: '100%',
            height: 300,
          },
          legend: {
            position: 'bottom',
          },
        },
      }],
      labels: [
        `Active Kunden ${infoData?.value?.coachingTotals?.activeCoaching}`,
        `Neu Kunden ${infoData?.value?.coachingTotals?.newCoaching}`,
        `Abgeschlossen Kunden ${infoData?.value?.coachingTotals?.finishedCoaching}`,
      ],
      plotOptions: {
        pie: {
          donut: {
            ...chartOptions.value.plotOptions.pie.donut,
            labels: {
              ...chartOptions.value.plotOptions.pie.donut.labels,
              total: {
                ...chartOptions.value.plotOptions.pie.donut.labels.total,
                formatter: () => totals.toString(),
              },
            },
          },
        },
      },
    };
  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(() => {
  CloseToast();
  getInfoData();
});
</script>

<style scoped>

::v-deep(.apexcharts-legend) {
  display: block !important;
}

</style>